import React, { useState, useEffect } from 'react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import AfipConfigModal from '../components/modals/AfipConfigModal';
import NotificationSettingsModal from '../components/modals/NotificationSettingsModal';
import ChatRoleSettingsModal from '../components/modals/ChatRoleSettingsModal';
import {
  Save,
  Moon,
  Sun,
  Calculator,
  Bell,
  MessageCircle,
  Users,
  Shield,
  Crown,
  Settings as SettingsIcon,
  Database,
  Zap,
  BarChart3,
  Mail
} from 'lucide-react';
import { useAppStore } from '../store';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { NotificationService } from '../utils/notificationService';

const Settings: React.FC = () => {
  const [expressCosts, setExpressCosts] = useState({
    remeraCost: '0',
    buzoCost: '0',
    estampadoCost: '0',
  });
  const [secondaryEmail, setSecondaryEmail] = useState<string>('');
  const [savingSecondary, setSavingSecondary] = useState<boolean>(false);
  const [sendingTestEmail, setSendingTestEmail] = useState<boolean>(false);

  const [isAfipModalOpen, setIsAfipModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isChatRoleModalOpen, setIsChatRoleModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { darkMode, toggleDarkMode } = useAppStore();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();
  
  // Fetch express costs on component mount
  useEffect(() => {
    const fetchExpressCosts = async () => {
      try {
        const { data, error } = await supabase
          .from('express_costs')
          .select('*')
          .limit(1);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          setExpressCosts({
            remeraCost: data[0].remera_cost?.toString() || '0',
            buzoCost: data[0].buzo_cost?.toString() || '0',
            estampadoCost: data[0].estampado_cost?.toString() || '0',
          });
        }
      } catch (error) {
        console.error('Error fetching express costs:', error);
      }
    };
    
    fetchExpressCosts();
    // Load current user's secondary email
    (async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth.user?.id;
        if (!userId) return;
        const { data } = await supabase
          .from('users')
          .select('secondary_email')
          .eq('id', userId)
          .maybeSingle();
        setSecondaryEmail(data?.secondary_email || '');
      } catch (e) {
        console.error('Error cargando email secundario:', e);
      }
    })();
  }, []);
  
  const handleExpressCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setExpressCosts({
      ...expressCosts,
      [name]: value,
    });
  };

  const handleAfipConfig = (config: any) => {
    console.log('AFIP config:', config);
  };

  const handleGoToQuoteInputs = () => {
    navigate('/configuracion/cotizador');
  };

  const handleNavigateTo = (path: string) => {
    navigate(path);
  };
  
  const handleSaveExpressCosts = async () => {
    setIsLoading(true);
    try {
      // Convert string values to numbers
      const remeraCost = parseFloat(expressCosts.remeraCost) || 0;
      const buzoCost = parseFloat(expressCosts.buzoCost) || 0;
      const estampadoCost = parseFloat(expressCosts.estampadoCost) || 0;
      
      // Check if record exists
      const { data, error: checkError } = await supabase
        .from('express_costs')
        .select('id')
        .limit(1);
      
      if (checkError) throw checkError;
      
      if (data && data.length > 0) {
        // Update existing record
        const { error } = await supabase
          .from('express_costs')
          .update({
            remera_cost: remeraCost,
            buzo_cost: buzoCost,
            estampado_cost: estampadoCost,
            updated_at: new Date().toISOString()
          })
          .eq('id', data[0].id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('express_costs')
          .insert([{
            remera_cost: remeraCost,
            buzo_cost: buzoCost,
            estampado_cost: estampadoCost
          }]);
        
        if (error) throw error;
      }
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Actualización de costos Express',
          entity_type: 'express_costs',
          entity_id: null,
          details: `Costos actualizados: Remera $${remeraCost}, Buzo $${buzoCost}, Estampado $${estampadoCost}`,
          action_type: 'update'
        }]);
      
      toast.success('Costos Express actualizados', 'Los costos han sido actualizados correctamente');
    } catch (error: any) {
      console.error('Error saving express costs:', error);
      toast.error('Error al guardar', error.message || 'Ha ocurrido un error al guardar los costos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSecondaryEmail = async () => {
    try {
      setSavingSecondary(true);
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) return;
      const normalized = secondaryEmail.trim() || null;
      const { error } = await supabase
        .from('users')
        .update({ secondary_email: normalized })
        .eq('id', userId);
      if (error) throw error;

      // Limpiar la caché del email secundario para que los cambios se reflejen inmediatamente
      NotificationService.clearSecondaryEmailCache(userId);

      toast.success('Email secundario guardado', normalized ? 'Usaremos también este email para notificaciones' : 'Se eliminó el email secundario');
    } catch (error: any) {
      console.error('Error guardando email secundario:', error);
      toast.error('Error al guardar', error.message || 'No se pudo guardar el email secundario');
    } finally {
      setSavingSecondary(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTestEmail(true);
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      const userEmail = auth.user?.email;

      if (!userId || !userEmail) {
        toast.error('Error', 'No se pudo obtener la información del usuario');
        return;
      }

      // Obtener el email secundario del usuario
      const { data: userData } = await supabase
        .from('users')
        .select('secondary_email')
        .eq('id', userId)
        .maybeSingle();

      const secondaryEmailValue = userData?.secondary_email;

      // Crear una notificación de prueba
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: 'system',
          title: 'Prueba de notificación por email',
          message: 'Este es un email de prueba para verificar que el sistema de notificaciones funciona correctamente.',
          read: false,
          created_at: new Date().toISOString()
        });

      if (notificationError) throw notificationError;

      toast.success(
        'Emails de prueba enviados',
        secondaryEmailValue
          ? `Se enviará un email a ${userEmail} y a ${secondaryEmailValue}`
          : `Se enviará un email a ${userEmail}`
      );
    } catch (error: any) {
      console.error('Error enviando email de prueba:', error);
      toast.error('Error al enviar', error.message || 'No se pudo enviar el email de prueba');
    } finally {
      setSendingTestEmail(false);
    }
  };
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Configuración del Sistema</h1>
        <p className="text-gray-600 dark:text-gray-400">Gestiona todas las configuraciones y accesos del sistema</p>
      </div>

      {/* Main Configuration Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Management */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader 
            title="Gestión de Usuarios" 
            icon={<Users className="h-6 w-6 text-blue-600" />}
          />
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Administra usuarios, roles y permisos del sistema.
              </p>
              <Button 
                onClick={() => handleNavigateTo('/usuarios')}
                className="w-full justify-center"
                icon={<Users className="h-5 w-5" />}
              >
                Gestionar Usuarios
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Client Portals */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader 
            title="Portales de Clientes" 
            icon={<Shield className="h-6 w-6 text-green-600" />}
          />
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configura y gestiona los portales de acceso para clientes.
              </p>
              <Button 
                onClick={() => handleNavigateTo('/portales-clientes')}
                className="w-full justify-center"
                icon={<Shield className="h-5 w-5" />}
              >
                Gestionar Portales
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Club Productex */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader 
            title="Club Productex®" 
            icon={<Crown className="h-6 w-6 text-yellow-600" />}
          />
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configura el programa de fidelidad y beneficios del Club Productex.
              </p>
              <Button 
                onClick={() => handleNavigateTo('/club-productex-config')}
                className="w-full justify-center"
                icon={<Crown className="h-5 w-5" />}
              >
                Configurar Club
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* System Settings */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader 
            title="Configuración del Sistema" 
            icon={<SettingsIcon className="h-6 w-6 text-purple-600" />}
          />
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configuraciones generales, notificaciones y preferencias.
              </p>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsNotificationModalOpen(true)}
                  icon={<Bell className="h-4 w-4" />}
                  className="w-full justify-start"
                >
                  Notificaciones
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsChatRoleModalOpen(true)}
                  icon={<MessageCircle className="h-4 w-4" />}
                  className="w-full justify-start"
                >
                  Configuración del Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAfipModalOpen(true)}
                  icon={<Database className="h-4 w-4" />}
                  className="w-full justify-start"
                >
                  Integración AFIP
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Configuration */}
      <Card>
        <CardHeader 
          title="Configuración de Negocio" 
          icon={<BarChart3 className="h-6 w-6 text-indigo-600" />}
        />
        <CardContent>
          <div className="space-y-6">
            {/* Express Costs */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Zap className="h-5 w-5 text-orange-600 mr-2" />
                Costos Express
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Configure los costos base para productos Express. Estos valores se utilizarán para calcular el margen bruto en el dashboard.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Costo Base Remera"
                  id="remeraCost"
                  name="remeraCost"
                  type="number"
                  value={expressCosts.remeraCost}
                  onChange={handleExpressCostChange}
                  required
                  min="0"
                  step="0.01"
                />
                
                <Input
                  label="Costo Base Buzo"
                  id="buzoCost"
                  name="buzoCost"
                  type="number"
                  value={expressCosts.buzoCost}
                  onChange={handleExpressCostChange}
                  required
                  min="0"
                  step="0.01"
                />
                
                <Input
                  label="Costo Base Estampado"
                  id="estampadoCost"
                  name="estampadoCost"
                  type="number"
                  value={expressCosts.estampadoCost}
                  onChange={handleExpressCostChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={handleSaveExpressCosts}
                  icon={<Save className="h-5 w-5" />}
                  disabled={isLoading}
                >
                  {isLoading ? 'Guardando...' : 'Guardar Costos'}
                </Button>
              </div>
            </div>

            {/* Quote Configuration */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                Configuración del Cotizador
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Configura los parámetros y precios base para el sistema de cotización.
              </p>
              <Button
                variant="outline"
                onClick={handleGoToQuoteInputs}
                icon={<Calculator className="h-5 w-5" />}
              >
                Configurar Cotizador
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader 
          title="Preferencias de Usuario" 
          icon={<SettingsIcon className="h-6 w-6 text-gray-600" />}
        />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Modo Oscuro</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Cambiar entre tema claro y oscuro
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                <span className="ml-3">
                  {darkMode ? <Moon className="h-4 w-4 text-gray-300" /> : <Sun className="h-4 w-4 text-gray-700" />}
                </span>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Alertas de Saldo Bajo</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Recibir notificaciones cuando el saldo esté bajo
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Secundario de Notificaciones */}
      <Card>
        <CardHeader 
          title="Email secundario de notificaciones" 
          icon={<Bell className="h-6 w-6 text-gray-600" />}
        />
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Si lo completas, además del email principal, también enviaremos notificaciones a este email.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <Input
                label="Email secundario"
                id="secondaryEmail"
                name="secondaryEmail"
                type="email"
                value={secondaryEmail}
                onChange={(e) => setSecondaryEmail(e.target.value)}
                placeholder="nombre@empresa.com"
              />
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <Button onClick={handleSaveSecondaryEmail} icon={<Save className="h-5 w-5" />} disabled={savingSecondary}>
                  {savingSecondary ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Prueba el sistema de envío de emails. Se enviará una notificación de prueba a tu email principal{secondaryEmail ? ' y al email secundario' : ''}.
              </p>
              <Button
                variant="outline"
                onClick={handleSendTestEmail}
                icon={<Mail className="h-5 w-5" />}
                disabled={sendingTestEmail}
              >
                {sendingTestEmail ? 'Enviando...' : 'Enviar email de prueba'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <AfipConfigModal
        isOpen={isAfipModalOpen}
        onClose={() => setIsAfipModalOpen(false)}
        onSave={handleAfipConfig}
      />
      
      <NotificationSettingsModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
      
      <ChatRoleSettingsModal
        isOpen={isChatRoleModalOpen}
        onClose={() => setIsChatRoleModalOpen(false)}
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default Settings;